import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { axiosClient } from '../axiosClient';
import { CoordinatesType, adReferencesFieldsResponseType } from '@/types/dataTypes';
import { ProjectType } from '@/types/dataTypes';
import { headers } from '../queryClient';
import { ProjectResponseType } from '@/types/apiTypes';
import { getPolygonApiData } from '@/api/apiHelpers';
import { squareSideType } from '@/types/dataTypes';
import { SoldSearchParamsCriteriaType, AdsSearchParamsCriteriaType } from '@/types/apiTypes';

interface Props {
  coordinates: CoordinatesType;
  soldParameters?: SoldSearchParamsCriteriaType;
  adsParameters?: AdsSearchParamsCriteriaType;
  marketParameters?: any;
  project: ProjectType;
  marketSquareside: squareSideType;
  columnOption: string | number;
}

function mapRefDataAd(project: ProjectType): adReferencesFieldsResponseType {
  const indexedArray: { [key: string]: boolean } = {};
  project.adsTableColumns.forEach(item => {
    indexedArray[item.id] = item.isVisible;
  });
  const obj: adReferencesFieldsResponseType = {
    address_field: indexedArray["street"],
    registered_date_field: indexedArray["registered_date"],
    advertising_days_field: indexedArray["advertising_days"],
    construction_year_field: indexedArray["constructionYear"],
    living_area_field: indexedArray["livingArea"],
    rooms_field: indexedArray["rooms"],
    floors_field: indexedArray["floorFloors"],
    plot_area_field: indexedArray["plot_area"],
    ancillary_area_field: indexedArray["ancillary_area"],
    price_field: indexedArray["price"],
    price_sqm_field: indexedArray["price_sqm"],
    monthly_fee_field: indexedArray["monthly_fee"],
    ownership_type_field: indexedArray["ownership_type"],
    firm_field: indexedArray["firm"],
    url_field: indexedArray["pdf"],
    home_type_field: indexedArray["homeType"],
  }
  return obj;

}

const useSaveProject = (
  options?: UseMutationOptions<ProjectResponseType, Error, Props>
) => {
  return useMutation({
    mutationFn: async ({
      coordinates,
      adsParameters,
      soldParameters,
      marketParameters,
      marketSquareside,
      project,
      columnOption
    }: Props): Promise<ProjectResponseType> => {
      const { lat, lng } = coordinates;
      const selectedSoldIds = project.soldTableIds.selected
        ? Array.from(project.soldTableIds.selected)
        : [];

      const excludedSoldIds = project.soldTableIds.excluded
        ? Array.from(project.soldTableIds.excluded)
        : [];

      const selectedAdsIds = project.adsTableIds.selected
        ? Array.from(project.adsTableIds.selected)
        : [];

      const excludedAdsIds = project.adsTableIds.excluded
        ? Array.from(project.adsTableIds.excluded)
        : [];

      let sale_references_sort = null;
      if (project.sale_references_sort) {
        sale_references_sort = {
          field: project.sale_references_sort.id,
          sort_order: project.sale_references_sort.desc ? "DESC" : "ASC"
        }
      }
      const body = {
        id: project.id,
        name: project.name,
        sale_references_sort: sale_references_sort,
        ad_references_search_criteria: {
          ...adsParameters,
          selected_ids: selectedAdsIds,
          excluded_ids: excludedAdsIds,
          wgs84lat: lat?.toString() || null,
          wgs84lon: lng?.toString() || null,
          polygons: getPolygonApiData(project.adsPolygons),
        },
        sale_references_search_criteria: {
          ...soldParameters,
          selected_ids: selectedSoldIds,
          excluded_ids: excludedSoldIds,
          wgs84lat: lat?.toString() || null,
          wgs84lon: lng?.toString() || null,
          polygons: getPolygonApiData(project.soldPolygons),
        },
        ad_references_fields: mapRefDataAd(project),
        sale_references_category: columnOption,
        ads_market_share_search_criteria: {
          ...marketParameters,
          ...marketSquareside,
          polygons: getPolygonApiData(project.marketPolygons),
        },
        index_normalized_search_criteria: {
          ...project.indexValues,
        },
        index_normalized_fields: project.index_normalized_fields
      };

      mapRefDataAd(project);
      const URL = import.meta.env.VITE_API_PROJECTS_URL;
      const { data } = await axiosClient.post<ProjectResponseType>(
        URL,
        body,
        headers
      );
      return data;
    },
    ...options,
  });

};

export default useSaveProject;
